/**
 * Collab Details copy per state: the status pill (hero) and the CTA bar title. Keyed by the
 * `nextStepKey` from COLLAB_STATE_MAP and by the past statuses, so it never decides which state
 * shows — collabUtils' getCollabStateKey still does that.
 */
export type StatusTone = 'green' | 'red' | 'grey' | 'dark';

export interface CollabStatusCopy {
  pill: string;
  tone: StatusTone;
  title: string;
  urgent?: boolean;
}

const STEP_COPY: Record<string, CollabStatusCopy> = {
  requestWaitlist: { pill: 'Campaign full', tone: 'grey', title: 'Campaign is full' },
  campaignFullSubmitOrWaitlist: { pill: 'Campaign full', tone: 'grey', title: 'Campaign is full' },
  inStoreDatesAndTimesCampaignEnded: { pill: 'Campaign ended', tone: 'grey', title: 'Campaign ended' },
  requestExtension: { pill: 'Extension needed', tone: 'red', title: 'Extension needed' },
  datesPassedSubmitOrExtension: { pill: 'Extension needed', tone: 'red', title: 'Extension needed' },
  waitingBrandApproval: { pill: 'Requested', tone: 'grey', title: 'Waiting on brand' },
  waitingBrandCounter: { pill: 'Your counter', tone: 'grey', title: 'Waiting on brand' },
  orderProduct: { pill: 'Add address', tone: 'red', title: 'Add shipping address' },
  messageBrand: { pill: 'Direct', tone: 'grey', title: 'Coordinate with the brand' },
  scheduleRetailCollab: { pill: 'Schedule', tone: 'red', title: 'Pick a store and date' },
  readyToPost: { pill: 'Buy product', tone: 'green', title: 'Buy the product' },
  scheduleCollabInStore: { pill: 'Schedule', tone: 'red', title: 'Schedule your visit' },
  visitScheduled: { pill: 'Visit booked', tone: 'green', title: 'Visit booked' },
  redeemReward: { pill: 'Redeem now', tone: 'dark', title: 'Redeem now' },
  markVisitComplete: { pill: 'Visit passed', tone: 'grey', title: 'Did you make it?' },
  viewReward: { pill: 'Reward active', tone: 'green', title: 'Reward active' },
  viewOrderStatus: { pill: 'Order placed', tone: 'grey', title: 'Order in progress' },
  confirmDelivery: { pill: 'On the way', tone: 'green', title: 'Order on the way' },
  productReceived: { pill: 'Shipping', tone: 'grey', title: 'Waiting for your product' },
  submitContent: { pill: 'Content due', tone: 'red', title: 'Submit your content', urgent: true },
  resubmitContent: { pill: 'Edits requested', tone: 'red', title: 'Edits requested', urgent: true },
  awaitingVerification: { pill: 'In review', tone: 'grey', title: 'Awaiting verification' },
};

const PAST_COPY: Record<string, CollabStatusCopy> = {
  collabCompleted: { pill: 'Completed', tone: 'green', title: 'Completed' },
  collabRejected: { pill: 'Declined', tone: 'grey', title: 'Declined' },
  collabCanceled: { pill: 'Canceled', tone: 'grey', title: 'Canceled' },
  collabExpired: { pill: 'Expired', tone: 'grey', title: 'Expired' },
  clientRejected: { pill: 'Not accepted', tone: 'grey', title: 'Not accepted' },
  clientCanceled: { pill: 'Canceled', tone: 'red', title: 'Canceled by brand' },
};

/** Past statuses win; otherwise the active step's copy; null when there's nothing to say. */
export const getCollabStatusCopy = (status: string, nextStepKey?: string): CollabStatusCopy | null =>
  PAST_COPY[status] ?? (nextStepKey ? STEP_COPY[nextStepKey] ?? null : null);

export const STATUS_PILL_COLORS: Record<StatusTone, { bg: string; color: string }> = {
  green: { bg: '#E4F3CC', color: '#2C5208' },
  red: { bg: '#FBE1DC', color: '#A1260F' },
  grey: { bg: '#F0F0EE', color: '#3E3E42' },
  dark: { bg: '#0E0E0E', color: '#C6F432' },
};
